// StockValuation.jsx
// Closing stock value per branch and item as of a date. Valuing every item at every branch is
// too slow to show on screen, so it runs as a background report (STOCK_VALUATION on
// server-postgres) and the Excel file turns up here and on My Reports.
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
  TextField, Typography,
} from "@mui/material";
import dayjs from "dayjs";
import ReportRequestsTable from "../backgroundReports/ReportRequestsTable";
import { requestReport, useReportRequests } from "../backgroundReports/reportRequestsApi";

const REPORT_TYPE = "STOCK_VALUATION";

// Branches assigned to the user, from the login claims; null when an older login didn't store
// them. Only narrows the list - the server refuses any other branch on its own.
const assignedBranches = () => {
  try {
    const list = JSON.parse(localStorage.getItem("allowedBranches"));
    return Array.isArray(list) ? list : null;
  } catch {
    return null;
  }
};

export default function StockValuation() {
  const [branches, setBranches] = useState([]);
  const [limited, setLimited] = useState(false);
  const [branchCode, setBranchCode] = useState("ALL");
  const [asOfDate, setAsOfDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const { requests, reload: loadRequests } = useReportRequests(REPORT_TYPE);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const res = await fetch(`/api/${localStorage.getItem("tenancyId")}/branches`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
        });
        const br = res.ok ? await res.json() : [];
        const list = Array.isArray(br) ? br : br.branches || br.data || [];
        const all = [...new Map(list.map((b) => [b.branchCode, b])).values()];
        const assigned = assignedBranches();
        const mine = assigned ? all.filter((b) => assigned.includes(b.branchCode)) : all;
        setBranches(mine);
        setLimited(mine.length < all.length);
      } catch (e) {
        console.error("Error loading branches:", e);
      }
    };
    loadBranches();
  }, []);

  const submit = async () => {
    if (!asOfDate) {
      setMessage({ severity: "error", text: "Choose an As of date." });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    const params = { asOfDate };
    if (branchCode && branchCode !== "ALL") params.branchCode = branchCode;
    const { ok, message: text } = await requestReport(REPORT_TYPE, params);
    setMessage({ severity: ok ? "success" : "error", text });
    if (ok) loadRequests();
    setSubmitting(false);
  };

  return (
    <Box p={3}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>Stock Valuation</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Closing stock on the chosen date for each branch and item, valued at the same rate as the Balance Sheet.
          It runs in the background; download the Excel file below or from My Reports when it is ready.
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            label="As of Date"
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Branch</InputLabel>
            <Select value={branchCode} label="Branch" onChange={(e) => setBranchCode(e.target.value)}>
              <MenuItem value="ALL">{limited ? "All my branches" : "All branches"}</MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.branchCode} value={b.branchCode}>
                  {b.branchName && b.branchName !== b.branchCode ? `${b.branchCode} - ${b.branchName}` : b.branchCode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : "Run in background"}
          </Button>
        </Box>
        {message && (
          <Alert severity={message.severity} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">My Requests</Typography>
          <Box>
            <Button size="small" component={RouterLink} to="/my-reports">All my reports</Button>
            <Button size="small" onClick={loadRequests}>Refresh</Button>
          </Box>
        </Box>
        <ReportRequestsTable requests={requests} />
      </Paper>
    </Box>
  );
}
